import { useMemo, useState } from "react";
import { Card, DataTable, Badge, Button, Text, Link, Tooltip, EmptyState, BlockStack, InlineStack } from "@shopify/polaris";
import { formatToCurrency } from "../../../shared/components/functions";
import { buildWorkbookXml, downloadWorkbook, excelCell, excelRow } from "../../../shared/components/excelExport";

// The grouped buy list: one row per part over the whole horizon, matching what
// the weekly report e-mails to production. Grouping happens server-side
// (services/mrpBuyList.js) so screen, supplier plan and e-mail stay identical.

const buildSheetRows = (parts) => {
  const rows = [
    excelRow([excelCell("Materials to order", "Title")]),
    excelRow([excelCell(`Generated ${new Date().toISOString().substring(0, 10)} — one line per part, all demand in the horizon combined`)]),
    "<Row />",
    excelRow([
      excelCell("Part ID", "Header"),
      excelCell("Reference", "Header"),
      excelCell("Description", "Header"),
      excelCell("Supplier", "Header"),
      excelCell("Qty to order", "Header"),
      excelCell("Order by", "Header"),
      excelCell("First needed", "Header"),
      excelCell("Unit price", "Header"),
      excelCell("Total value", "Header"),
      excelCell("Late", "Header"),
      excelCell("Driven by", "Header"),
    ]),
  ];
  parts.forEach((part) => {
    rows.push(
      excelRow([
        excelCell(part.productId, undefined, true),
        excelCell(part.productRef || ""),
        excelCell(part.productName || ""),
        excelCell(part.supplierName || ""),
        excelCell(part.totalSuggestedQty, undefined, true),
        excelCell(part.firstOrderByDate || "", part.isLate ? "Late" : undefined),
        excelCell(part.firstNeedDate || ""),
        excelCell(part.unitPrice ?? "", undefined, true),
        excelCell(part.totalValue, undefined, true),
        excelCell(part.isLate ? "LATE" : "", part.isLate ? "Late" : undefined),
        excelCell((part.sources || []).join(" · ")),
      ])
    );
  });
  rows.push("<Row />");
  const totalValue = parts.reduce((sum, part) => sum + (part.totalValue || 0), 0);
  rows.push(excelRow([excelCell("Total", "Group"), excelCell(""), excelCell(""), excelCell(""), excelCell(""), excelCell(""), excelCell(""), excelCell(""), excelCell(totalValue, "Group", true), excelCell(""), excelCell("")]));
  return rows.join("");
};

const exportParts = (parts) => {
  const xml = buildWorkbookXml([{ name: "To order", rowsXml: buildSheetRows(parts) }]);
  downloadWorkbook(xml, `to-order-${new Date().toISOString().substring(0, 10)}.xls`);
};

const linesTooltip = (part) =>
  part.lines.map((line) => `${line.suggestedQty} for ${line.needDate || "?"} (order by ${line.orderByDate || "?"})`).join(" · ");

export function ToOrderList({ parts }) {
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [lateOnly, setLateOnly] = useState(false);

  const filtered = useMemo(
    () =>
      parts.filter((part) => {
        if (lateOnly && !part.isLate) return false;
        if (typeFilter === "ALL") return true;
        return (part.suggestionTypes || []).includes(typeFilter);
      }),
    [parts, typeFilter, lateOnly]
  );

  if (!parts.length) {
    return (
      <Card>
        <EmptyState heading="Nothing to order" image="">
          <p>No net requirements in the planning horizon. Stock plus confirmed inbound covers all demand.</p>
        </EmptyState>
      </Card>
    );
  }

  const totalValue = filtered.reduce((sum, part) => sum + (part.totalValue || 0), 0);
  const lateCount = filtered.filter((part) => part.isLate).length;

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between" blockAlign="center">
        <InlineStack gap="200">
          <Button pressed={typeFilter === "ALL"} onClick={() => setTypeFilter("ALL")}>
            All
          </Button>
          <Button pressed={typeFilter === "MRP"} onClick={() => setTypeFilter("MRP")}>
            Time-phased
          </Button>
          <Button pressed={typeFilter === "REORDER_POINT"} onClick={() => setTypeFilter("REORDER_POINT")}>
            Reorder point
          </Button>
          <Button pressed={lateOnly} onClick={() => setLateOnly(!lateOnly)}>
            Late only
          </Button>
        </InlineStack>
        <InlineStack gap="200" blockAlign="center">
          {lateCount > 0 && <Badge tone="critical">{`${lateCount} late`}</Badge>}
          <Badge tone="info">{formatToCurrency(totalValue)}</Badge>
          <Button onClick={() => exportParts(filtered)}>Export Excel</Button>
        </InlineStack>
      </InlineStack>

      <Card>
        <DataTable
          verticalAlign="middle"
          increasedTableDensity
          columnContentTypes={["text", "text", "text", "text", "numeric", "text", "text", "numeric", "numeric", "text"]}
          headings={[<b>Part</b>, <b>Reference</b>, <b>Description</b>, <b>Supplier</b>, <b>Qty to order</b>, <b>Order by</b>, <b>First needed</b>, <b>Unit price</b>, <b>Value</b>, <b>Driven by</b>]}
          rows={filtered.map((part) => [
            <Link removeUnderline url={`/products/${part.productId}`}>
              {part.productId}
            </Link>,
            part.productRef || "",
            String(part.productName || "").substring(0, 45),
            part.supplierId ? (
              <Link removeUnderline url={`/companies/${part.supplierId}`}>
                {part.supplierName}
              </Link>
            ) : (
              part.supplierName
            ),
            <Tooltip content={linesTooltip(part)}>
              <Text as="span" fontWeight="bold">
                {part.totalSuggestedQty}
              </Text>
            </Tooltip>,
            part.isLate ? (
              <InlineStack gap="100" blockAlign="center">
                {part.firstOrderByDate || ""}
                <Badge tone="critical">Late</Badge>
              </InlineStack>
            ) : (
              part.firstOrderByDate || ""
            ),
            part.firstNeedDate || "",
            part.unitPrice ? formatToCurrency(part.unitPrice) : "",
            part.totalValue ? formatToCurrency(part.totalValue) : "",
            <Tooltip content={(part.sources || []).join(" · ") || "—"}>
              <Badge size="small" tone={(part.suggestionTypes || []).includes("REORDER_POINT") ? "attention" : "info"}>
                {part.lineCount > 1 ? `${part.lineCount} lines` : (part.sources || [])[0]?.substring(0, 20) || "1 line"}
              </Badge>
            </Tooltip>,
          ])}
        />
      </Card>
    </BlockStack>
  );
}
